// The eleven-step co-pilot quiz, always shown inside QuizModal (the /get-started
// CTAs, the browse pop-up on the board and job pages). All figures come from
// quiz_bootstrap(); submission reuses the live intake path
// (intake_submissions + create-job-alert) so the day-0 welcome and the alert
// fire unchanged.
//
// Honesty rules:
//  - a step that cannot claim something says so (no-match states per step)
//  - the alert is exactly what create-job-alert stores: title, location or
//    remote, and a frequency. Every other answer shapes the plan on screen and
//    the copy must never claim the alert filters on it
//  - the setup pass after email runs only once the alert is saved, and
//    describes what is actually being computed, never a fake scrape
//  - salary is the only optional question
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { siteEvents } from "@/lib/siteEvents";
import {
  matchCities, matchRoles, money, num, TOOL_GROUPS, writeProfile,
  type QuizBootstrap, type QuizCity, type QuizRole,
} from "./useQuizData";

const INK = "#0F2137";
const TEXT = "#2B3D52";
const MUTED = "#67788C";
const FAINT = "#8697A8";
const GREEN = "#12855A";
const GREEN_TXT = "#0E7C52";
const LIME = "#2BC97F";
const SURFACE = "#F7FAFC";
const GREEN_BG = "#F1FAF5";
const HAIR = "#EEF2F7";
const BORDER = "#D6DFE8";
const RED_BG = "#FDF3F2";
const RED_TXT = "#A3322A";

type Country = "US" | "CA";

interface Answers {
  role: QuizRole | null;
  city: QuizCity | null;
  /** Set when the city step is skipped, so a Canadian is never read as US. */
  country: Country | null;
  setup: string | null;
  years: string | null;
  move: string | null;
  software: string[];
  cpa: string | null;
  stage: string | null;
  block: string | null;
  salary: string;
  email: string;
}

const EMPTY: Answers = {
  role: null, city: null, country: null, setup: null, years: null, move: null,
  software: [], cpa: null, stage: null, block: null, salary: "", email: "",
};

// Step names for analytics and the screen-reader announcement.
const STEP_NAMES = ["title", "city", "setup", "years", "move", "systems", "cpa", "stage", "block", "salary", "email", "working", "plan"];
const STEP_QUESTIONS = [
  "What is your job title?", "Which city are you in?", "How do you want to work?",
  "How long have you been doing it?", "What is the next move?", "Which of these have you used?",
  "Do you hold a CPA?", "Where are you in this?", "What is actually getting in the way?",
  "What are you paid now?", "Where should we send your alert?", "Setting up", "Your plan",
];

/** Stage answer to alert frequency. send-job-alerts runs a Monday weekly pass
 *  (send-weekly-job-alerts cron) and a 6-day dedup window for weekly rows. */
function frequencyFor(stage: string | null): "daily" | "weekly" {
  return stage === "active" ? "daily" : "weekly";
}

const S = {
  card: { display: "flex", flexDirection: "column", minHeight: "100%", fontFamily: '"Manrope", "Gilroy", system-ui, sans-serif' } as React.CSSProperties,
  progressRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9, minHeight: 28 } as React.CSSProperties,
  progressLabel: { fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: FAINT } as React.CSSProperties,
  track: { height: 3, background: HAIR, borderRadius: 999, marginBottom: 24 } as React.CSSProperties,
  q: { fontSize: 24, fontWeight: 800, lineHeight: 1.18, letterSpacing: "-0.028em", color: INK, margin: "0 0 7px", outline: "none" } as React.CSSProperties,
  sub: { fontSize: 14, lineHeight: 1.55, color: MUTED, margin: "0 0 20px" } as React.CSSProperties,
  label: { display: "block", fontSize: 12.5, fontWeight: 600, color: MUTED, marginBottom: 7 } as React.CSSProperties,
  input: { width: "100%", height: 46, padding: "0 14px", border: `1px solid ${BORDER}`, borderRadius: 11, background: "#fff", fontFamily: "inherit", fontSize: 16, fontWeight: 500, color: TEXT } as React.CSSProperties,
  opt: { display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", columnGap: 12, alignItems: "center", width: "100%", minHeight: 50, padding: "12px 16px", background: "#fff", border: `1.5px solid ${BORDER}`, borderRadius: 11, cursor: "pointer", textAlign: "left", fontFamily: "inherit" } as React.CSSProperties,
  hit: { display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", columnGap: 12, alignItems: "center", width: "100%", padding: "12px 14px", background: "#fff", border: "none", borderBottom: `1px solid ${HAIR}`, cursor: "pointer", textAlign: "left", fontFamily: "inherit" } as React.CSSProperties,
  pill: { minHeight: 44, padding: "8px 12px", background: "#fff", border: `1.5px solid ${BORDER}`, borderRadius: 11, cursor: "pointer", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, color: TEXT, textAlign: "left" } as React.CSSProperties,
  primary: { width: "100%", minHeight: 48, background: INK, color: "#fff", border: "none", borderRadius: 11, fontFamily: "inherit", fontSize: 15, fontWeight: 700, cursor: "pointer" } as React.CSSProperties,
  ghost: { width: "100%", minHeight: 44, background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: FAINT } as React.CSSProperties,
  back: { display: "inline-flex", alignItems: "center", gap: 4, minHeight: 28, padding: "0 6px 0 0", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: MUTED } as React.CSSProperties,
};

// Hover only where a pointer can hover (no sticky borders after a tap), and a
// visible keyboard focus ring everywhere.
const QUIZ_CSS = `
.afq-sr { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
.afq input:focus { outline:none; border-color:${INK}; }
.afq input:focus-visible, .afq button:focus-visible, .afq a:focus-visible { outline:2px solid ${LIME}; outline-offset:2px; }
@media (hover:hover) {
  .afq-opt:hover, .afq-pill:hover { border-color:${INK} !important; }
  .afq-hit:hover { background:${GREEN_BG} !important; }
  .afq-back:hover, .afq-ghost:hover { color:${INK} !important; }
}
`;

export default function CopilotQuiz({ data, onDone, source = "unknown" }: { data: QuizBootstrap; onDone?: () => void; source?: string }) {
  const [step, setStep] = useState(0);
  const [hist, setHist] = useState<number[]>([]);
  const [a, setA] = useState<Answers>(EMPTY);
  const [roleQ, setRoleQ] = useState("");
  const [cityQ, setCityQ] = useState("");
  const [scanIdx, setScanIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [alertError, setAlertError] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const intakeSaved = useRef(false);
  const finished = useRef(false);
  const stepRef = useRef(0);
  const firstRender = useRef(true);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const goTo = useCallback((next: number) => {
    setHist((h) => [...h, stepRef.current]);
    setStep(next);
  }, []);
  const go = useCallback((patch: Partial<Answers>, next?: number) => {
    setA((prev) => ({ ...prev, ...patch }));
    goTo(next ?? stepRef.current + 1);
  }, [goTo]);
  const back = useCallback(() => {
    if (!hist.length) return;
    setStep(hist[hist.length - 1]);
    setHist(hist.slice(0, -1));
    setAlertError(false);
  }, [hist]);

  // Step view events, the screen-reader announcement and focus on each new
  // question (inputs keep their own autofocus).
  useEffect(() => {
    stepRef.current = step;
    siteEvents.act("quiz_step", { source, step, step_name: STEP_NAMES[step] });
    if (firstRender.current) { firstRender.current = false; return; }
    if (step !== 0 && step !== 1 && step !== 9 && step !== 10) headingRef.current?.focus();
  }, [step, source]);

  // Abandon: the dialog closed, or the tab went away, before the plan showed.
  useEffect(() => {
    const abandon = () => {
      if (finished.current) return;
      finished.current = true;
      siteEvents.act("quiz_abandon", { source, step: stepRef.current, step_name: STEP_NAMES[stepRef.current] });
    };
    window.addEventListener("pagehide", abandon);
    const pending = timers.current;
    return () => {
      window.removeEventListener("pagehide", abandon);
      pending.forEach(clearTimeout);
      abandon();
    };
  }, [source]);

  const roleHits = useMemo(() => matchRoles(data.roles, roleQ), [data.roles, roleQ]);
  const cityHits = useMemo(() => matchCities(data.cities, cityQ), [data.cities, cityQ]);

  const country: Country = a.city?.k ?? a.country ?? "US";
  const isCA = country === "CA";
  const cadBand = a.role ? data.cad[a.role.slug] : undefined;
  const band = a.role
    ? isCA && cadBand
      ? { p25: cadBand.p25, med: cadBand.med, p75: cadBand.p75, n: cadBand.n }
      : { p25: a.role.p25, med: a.role.med, p75: a.role.p75, n: a.role.n }
    : null;
  // A Canadian role without a CAD band falls back to USD figures; say so in
  // the place label rather than printing US numbers as Canadian ones.
  const moneyCA = !!(isCA && cadBand);
  const countryName = isCA ? "Canada" : "the United States";
  const place = a.city ? a.city.m : countryName;
  const recCount = a.city?.n ?? 0;
  const isRemote = a.setup === "remote";
  const roleName = a.role?.name ?? (roleQ.trim() || null);
  const freq = frequencyFor(a.stage);
  const alertScope = isRemote ? `remote roles in ${countryName}` : a.city ? `${a.city.c}${a.city.r ? ", " + a.city.r : ""}` : countryName;

  const runSetup = useCallback(() => {
    setStep(11);
    setScanIdx(0);
    timers.current.push(...[1, 2, 3, 4].map((i) => setTimeout(() => setScanIdx(i), i * 700)));
    timers.current.push(setTimeout(() => {
      finished.current = true;
      setStep(12);
    }, 5 * 700));
  }, []);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setAlertError(false);
    const email = a.email.trim();
    const location = isRemote ? null : a.city ? `${a.city.c}${a.city.r ? ", " + a.city.r : ""}` : null;
    siteEvents.act("quiz_submit", { source, frequency: freq, remote: isRemote, has_salary: !!a.salary });

    // Capture is best-effort and written once, even across alert retries.
    if (!intakeSaved.current) {
      try {
        const { error } = await supabase.from("intake_submissions" as never).insert({
          email,
          job_title: roleName,
          location,
          is_remote: isRemote,
          experience_level: a.years,
          is_cpa: a.cpa === "yes",
          answers: {
            version: "copilot-v2",
            role_slug: a.role?.slug ?? null,
            role_query: roleQ || null,
            metro: a.city?.m ?? null,
            country,
            setup: a.setup,
            years: a.years,
            move: a.move,
            software: a.software,
            cpa: a.cpa,
            stage: a.stage,
            block: a.block,
            frequency: freq,
            source,
            salary: a.salary ? Number(String(a.salary).replace(/[^0-9.]/g, "")) : null,
          },
        } as never);
        if (error) siteEvents.act("quiz_intake_error", { source });
        else intakeSaved.current = true;
      } catch {
        siteEvents.act("quiz_intake_error", { source });
      }
    }

    // The alert is the contract: if it did not save, say so and offer a retry
    // instead of playing the setup pass over a failure.
    let ok = false;
    try {
      const { error } = await supabase.functions.invoke("create-job-alert", {
        body: {
          jobTitle: roleName,
          location,
          isRemote,
          email,
          frequency: freq,
          source: "quiz",
        },
      });
      ok = !error;
    } catch {
      ok = false;
    }
    siteEvents.act(ok ? "quiz_alert_ok" : "quiz_alert_error", { source, frequency: freq });
    setSubmitting(false);
    if (!ok) {
      setAlertError(true);
      return;
    }

    // Baseline for the return surface on /get-started: what the market looked
    // like the day they set up. The band variable already resolved CAD.
    writeProfile({
      v: 1,
      at: new Date().toISOString(),
      role: a.role ? { slug: a.role.slug, name: a.role.name } : null,
      city: a.city,
      setup: a.setup,
      base: a.role && band
        ? { live: a.role.live, med: band.med, above: a.role.above, isCA: moneyCA }
        : null,
    });
    runSetup();
  }, [a, roleQ, roleName, submitting, isRemote, freq, country, band, moneyCA, source, runSetup]);

  const chapter = step >= 12 ? "Your plan" : step === 11 ? "Setting up" : step <= 2 ? "Your market" : step <= 6 ? "Your profile" : step <= 8 ? "Your search" : step === 9 ? "Your number" : "Your alert";

  const opt = (label: string, hint: string, patch: Partial<Answers>, next?: number) => (
    <button type="button" key={label} className="afq-opt" style={S.opt} onClick={() => go(patch, next)}>
      <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>{label}</span>
      {hint ? <span style={{ fontSize: 12.5, fontWeight: 600, color: FAINT, whiteSpace: "nowrap" }}>{hint}</span> : <span />}
    </button>
  );

  // Scan facts computed server-side into the same bootstrap cache. Optional:
  // an older cached payload has no scan section, and every line below must
  // stay true without it.
  const scan = a.role ? data.scan?.[a.role.slug] : undefined;
  const scanLines = [
    `Matching ${roleName ?? "your title"} against ${num(a.role?.live ?? data.corpus.live)} live postings`,
    a.software.length === 1 ? "Noting the system you named for your plan"
      : a.software.length ? `Noting the ${a.software.length} systems you named for your plan` : "Checking what the postings ask for",
    a.city ? `Counting recruiters who cover ${place}` : "Opening the recruiter directory",
    "Placing you in the advertised pay band",
    scan && scan.fresh > 0
      ? `Found ${num(scan.fresh)} ${a.role?.name} postings from the last 7 days`
      : `Your ${freq} alert is saved`,
  ];

  const salaryNum = Number(String(a.salary).replace(/[^0-9.]/g, "")) || 0;
  let verdict = "";
  if (band && salaryNum > 0) {
    if (salaryNum < band.p25) verdict = `You are below the 25th percentile. Three in four ${a.role?.name} postings advertise more, and the gap to the median is ${money(band.med - salaryNum, moneyCA)}.`;
    else if (salaryNum < band.med) verdict = `You sit between the 25th percentile and the median. Closing to the median is ${money(band.med - salaryNum, moneyCA)}.`;
    else if (salaryNum < band.p75) verdict = `You are above the median. The 75th percentile is ${money(band.p75, moneyCA)}.`;
    else verdict = `You are in the top quarter. Fewer than one in four ${a.role?.name} postings advertise more than this.`;
  }

  const fixText =
    a.block === "silence" ? "You said applications go unanswered. Lead with postings that name the systems you already use: a named system is the most common gate we can see in postings."
      : a.block === "level" ? "You want the next level. Search one rung above your current title, and put the postings that name your systems first."
        : a.block === "place" ? "You said nothing is near you. Filter the board to remote roles, and read each posting for how remote it really is: hybrid often means the office."
          : band ? `You said nothing pays enough. Sort the board by salary and start with postings that advertise ${money(band.med, moneyCA)} or more, the median for your title.`
            : "You said nothing pays enough. Sort the board by salary and start at the top, where the pay is advertised.";

  const plan = [
    band && { h: "Where to aim", b: `The measured rate for ${a.role?.name} in ${place} is ${money(band.med, moneyCA)}, across ${num(band.n)} priced postings.${isCA && !cadBand ? " These are US dollar figures: we do not yet have enough Canadian postings for this title." : ""}` },
    {
      h: "Front-run the market",
      b: (scan && scan.fresh > 0
        ? `${num(scan.fresh)} ${a.role?.name} postings appeared in the last 7 days alone` +
          // Examples are national USD figures; a CAD reader's plan stays in
          // one currency, so they get the count only.
          (!isCA && scan.ex && scan.ex.length > 0
            ? `, and this week's priced postings include ${scan.ex.reduce((a2, b2) => (b2.p > a2.p ? b2 : a2)).c} at ${money(scan.ex.reduce((a2, b2) => (b2.p > a2.p ? b2 : a2)).p, false)}`
            : "") + ". "
        : "") +
        `${num(data.corpus.direct)} of the postings we track were read straight from employer career sites, before any board had them. Your alert includes them.`,
    },
    band && salaryNum > 0 && { h: "What you are worth", b: verdict },
    band && !(salaryNum > 0) && { h: "What you are worth", b: "You skipped your current pay, so we compare you to the band rather than your own number. Take the quiz again with it any time and this becomes a gap, not a range." },
    { h: "Who to talk to", b: recCount > 0 ? `${recCount} recruiters in ${place} list your title among the roles they fill.` : `We track ${num(data.recruiters.total)} active recruiters across ${data.recruiters.metros} metros, each naming the roles they fill.` },
    a.block && { h: "What to fix", b: fixText },
  ].filter(Boolean) as { h: string; b: string }[];

  const matchesHref = roleName
    ? `/?q=${encodeURIComponent(roleName)}${isRemote ? "&remote=true" : ""}`
    : "/";

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (step === 0 && roleHits[0]) { setRoleQ(roleHits[0].name); go({ role: roleHits[0] }); }
    else if (step === 1 && cityHits[0]) { setCityQ(cityHits[0].c); go({ city: cityHits[0], country: cityHits[0].k }); }
    else if (step === 5) goTo(6);
    else if (step === 9) goTo(10);
    else if (step === 10 && /.+@.+\..+/.test(a.email) && !submitting) void submit();
  };

  const progress = Math.round(((Math.min(step, 12) + 1) / 13) * 100);
  const emailOk = /.+@.+\..+/.test(a.email);

  return (
    <form className="afq" style={S.card} onSubmit={onSubmit} noValidate>
      <style>{QUIZ_CSS}</style>
      <div className="afq-sr" aria-live="polite" aria-atomic="true">
        {step <= 10 ? `Step ${step + 1} of 11. ${STEP_QUESTIONS[step]}` : step === 11 ? "Setting up your alert" : "Your plan is ready"}
      </div>
      <div style={S.progressRow}>
        {step >= 1 && step <= 10 && hist.length > 0 ? (
          <button type="button" className="afq-back" style={S.back} onClick={back} aria-label="Back to the previous question">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
            Back
          </button>
        ) : (
          <span style={S.progressLabel}>{chapter}</span>
        )}
        <span style={S.progressLabel}>{step >= 12 ? "Done" : step === 11 ? "Working" : `Step ${step + 1} of 11`}</span>
      </div>
      <div style={S.track} aria-hidden="true"><div style={{ height: 3, width: `${progress}%`, background: LIME, borderRadius: 999 }} /></div>

      {step <= 10 && (
        <div style={{ flexGrow: 1 }}>
          {step === 0 && (
            <>
              <h2 ref={headingRef} tabIndex={-1} style={S.q}>What is your job title?</h2>
              <p style={S.sub}>Start typing. We match it to a measured role, not a keyword, so &quot;AP clerk&quot; and &quot;Accounts Payable Analyst&quot; land in the same place.</p>
              <label htmlFor="afq-title" className="afq-sr">Job title</label>
              <input id="afq-title" style={S.input} placeholder="Try Sr Accountant, AP analyst, FP&A" value={roleQ}
                autoComplete="organization-title" onChange={(e) => setRoleQ(e.target.value)} autoFocus />
              <div style={{ marginTop: 10 }}>
                {roleHits.map((r) => (
                  <button type="button" key={r.slug} className="afq-hit" style={S.hit} onClick={() => { setRoleQ(r.name); go({ role: r }); }}>
                    <span>
                      <span style={{ display: "block", fontSize: 15, fontWeight: 600, color: INK }}>{r.name}</span>
                      <span style={{ display: "block", fontSize: 12.5, color: FAINT, marginTop: 2 }}>{r.grp === "fpa_finance" ? "FP&A and finance" : r.grp === "ap_ar" ? "AP and AR" : r.grp.charAt(0).toUpperCase() + r.grp.slice(1)}</span>
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: GREEN, whiteSpace: "nowrap" }}>{num(r.live)} open</span>
                  </button>
                ))}
                {roleQ.trim().length >= 2 && roleHits.length === 0 && (
                  <div style={{ background: SURFACE, borderRadius: 11, padding: "16px 18px", marginTop: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginBottom: 5 }}>We do not measure that title yet</div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "#55677B" }}>We only publish a rate once there are enough live postings to stand behind it, and this title is short. Pick the closest match above, or keep going and your alert will use the title as you typed it.</div>
                    <button type="button" className="afq-ghost" style={{ ...S.ghost, textAlign: "left", paddingLeft: 0 }} onClick={() => go({ role: null })}>Continue with &quot;{roleQ.trim()}&quot;</button>
                  </div>
                )}
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <h2 ref={headingRef} tabIndex={-1} style={S.q}>Which city are you in?</h2>
              <p style={S.sub}>This sets the pay comparison, your currency, and which recruiters can actually place you.</p>
              <label htmlFor="afq-city" className="afq-sr">City</label>
              <input id="afq-city" style={S.input} placeholder="Try San Francisco, Plano, Mississauga" value={cityQ}
                autoComplete="address-level2" onChange={(e) => setCityQ(e.target.value)} autoFocus />
              <div style={{ marginTop: 10 }}>
                {cityHits.map((c, i) => (
                  <button type="button" key={`${c.c}-${c.m}-${i}`} className="afq-hit" style={S.hit} onClick={() => { setCityQ(c.c); go({ city: c, country: c.k }); }}>
                    <span>
                      <span style={{ display: "block", fontSize: 15, fontWeight: 600, color: INK }}>{c.c}{c.r ? `, ${c.r}` : ""}</span>
                      <span style={{ display: "block", fontSize: 12.5, color: FAINT, marginTop: 2 }}>{c.m} metro{c.k === "CA" ? ", paid in CAD" : ""}</span>
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: c.n > 0 ? GREEN : FAINT, whiteSpace: "nowrap" }}>{c.n > 0 ? `${c.n} recruiters` : "No recruiters yet"}</span>
                  </button>
                ))}
                {cityQ.trim().length >= 2 && cityHits.length === 0 && (
                  <div style={{ background: SURFACE, borderRadius: 11, padding: "16px 18px", marginTop: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: INK, marginBottom: 5 }}>We do not cover that city yet</div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "#55677B" }}>We map cities to metros so the pay comparison holds. Try the nearest large city, or pick your country below and we will use the national figures.</div>
                  </div>
                )}
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: MUTED, marginBottom: 8 }}>Fully remote, or not listed?</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8 }}>
                    <button type="button" className="afq-pill" style={S.pill} onClick={() => go({ city: null, country: "US", setup: "remote" }, 3)}>Fully remote, US</button>
                    <button type="button" className="afq-pill" style={S.pill} onClick={() => go({ city: null, country: "CA", setup: "remote" }, 3)}>Fully remote, Canada</button>
                    <button type="button" className="afq-pill" style={S.pill} onClick={() => go({ city: null, country: "US" })}>Elsewhere in the US</button>
                    <button type="button" className="afq-pill" style={S.pill} onClick={() => go({ city: null, country: "CA" })}>Elsewhere in Canada</button>
                  </div>
                </div>
              </div>
            </>
          )}
          {step === 2 && (<><h2 ref={headingRef} tabIndex={-1} style={S.q}>How do you want to work?</h2><p style={S.sub}>Remote changes both the pay band and the size of your market, so it is worth being honest here.</p><div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{opt("On site", "", { setup: "onsite" })}{opt("Hybrid", "", { setup: "hybrid" })}{opt("Remote only", "", { setup: "remote" })}{opt("Open to anything", "", { setup: "any" })}</div></>)}
          {step === 3 && (<><h2 ref={headingRef} tabIndex={-1} style={S.q}>How long have you been doing it?</h2><p style={S.sub}>Seniority moves the number more than any other answer.</p><div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{opt("Under 2 years", "", { years: "entry" })}{opt("2 to 5 years", "", { years: "mid" })}{opt("5 to 10 years", "", { years: "senior" })}{opt("More than 10", "", { years: "lead" })}</div></>)}
          {step === 4 && (<><h2 ref={headingRef} tabIndex={-1} style={S.q}>What is the next move?</h2><p style={S.sub}>Aim at the job you want next, not the one you have now. This shapes the plan you get at the end.</p><div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{opt("The same role, better paid", "", { move: "lateral" })}{opt("A step up in level", "", { move: "up" })}{opt("A different function", "", { move: "switch" })}{opt("Just watching the market", "", { move: "watch" })}</div></>)}
          {step === 5 && (
            <>
              <h2 ref={headingRef} tabIndex={-1} style={S.q}>Which of these have you used?</h2>
              <p style={S.sub}>Postings that name systems you already know are the ones worth your time. Your plan uses this.</p>
              <div role="group" aria-label="Systems you have used">
                {TOOL_GROUPS.map((g) => (
                  <div key={g.group} style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: FAINT, marginBottom: 8 }}>{g.group}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                      {g.items.map(([slug, label]) => {
                        const on = a.software.includes(slug);
                        return (
                          <button type="button" key={slug} aria-pressed={on} className={on ? undefined : "afq-pill"}
                            onClick={() => setA((p) => ({ ...p, software: on ? p.software.filter((x) => x !== slug) : [...p.software, slug] }))}
                            style={{ minHeight: 44, padding: "8px 14px", background: on ? GREEN_BG : "#fff", border: `1.5px solid ${on ? GREEN : BORDER}`, borderRadius: 999, cursor: "pointer", fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, color: on ? GREEN_TXT : TEXT }}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ position: "sticky", bottom: 0, background: "#fff", paddingTop: 10, paddingBottom: 2 }}>
                <button type="submit" style={S.primary}>
                  {a.software.length ? `Continue with ${a.software.length} selected` : "I use none of these"}
                </button>
              </div>
            </>
          )}
          {step === 6 && (<><h2 ref={headingRef} tabIndex={-1} style={S.q}>Do you hold a CPA?</h2><p style={S.sub}>We measure the premium inside each role rather than across all of them, so it only counts where it actually pays.</p><div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{opt("Yes", "", { cpa: "yes" })}{opt("No", "", { cpa: "no" })}{opt("Studying for it", "", { cpa: "studying" })}{opt("A different credential", "CMA, CA, ACCA", { cpa: "other" })}</div></>)}
          {step === 7 && (<><h2 ref={headingRef} tabIndex={-1} style={S.q}>Where are you in this?</h2><p style={S.sub}>This sets how often your alert arrives: daily while you are applying, weekly otherwise.</p><div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{opt("Actively applying", "daily alert", { stage: "active" })}{opt("Looking, not urgently", "weekly alert", { stage: "open" })}{opt("Happy, but curious", "weekly alert", { stage: "passive" })}{opt("Just starting out", "weekly alert", { stage: "new" })}</div></>)}
          {step === 8 && (<><h2 ref={headingRef} tabIndex={-1} style={S.q}>What is actually getting in the way?</h2><p style={S.sub}>Be blunt. Your plan leads with the fix for this one.</p><div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{opt("No replies to applications", "", { block: "silence" })}{opt("Nothing pays enough", "", { block: "pay" })}{opt("Nothing near me", "", { block: "place" })}{opt("Cannot get to the next level", "", { block: "level" })}</div></>)}
          {step === 9 && (
            <>
              <h2 ref={headingRef} tabIndex={-1} style={S.q}>What are you paid now?</h2>
              <p style={S.sub}>Optional, and the only question that is. Give it and we place you in the band instead of just showing you the band.</p>
              <label htmlFor="afq-salary" style={S.label}>Base salary, before bonus{moneyCA ? ", in CAD" : ""}</label>
              <input id="afq-salary" style={S.input} placeholder="70,000" inputMode="numeric" value={a.salary} autoFocus
                aria-describedby="afq-salary-note"
                onChange={(e) => setA((p) => ({ ...p, salary: e.target.value }))} />
              <button type="submit" style={{ ...S.primary, marginTop: 12 }}>Compare me to the market</button>
              <button type="button" className="afq-ghost" style={S.ghost} onClick={() => { setA((p) => ({ ...p, salary: "" })); goTo(10); }}>Skip this one</button>
              <p id="afq-salary-note" style={{ fontSize: 12, lineHeight: 1.5, color: FAINT, margin: "8px 0 0" }}>Your salary is never shown to employers or recruiters. It is only used to place you in the band.</p>
            </>
          )}
          {step === 10 && (
            <>
              <h2 ref={headingRef} tabIndex={-1} style={S.q}>Your {a.role?.name ?? "job search"} plan is ready</h2>
              <p style={S.sub}>
                Your plan comes next, plus a {freq} email alert for {roleName ?? "your title"} in {alertScope}. The rest of your answers shape the plan.
              </p>
              {band && (
                <div style={{ background: SURFACE, borderRadius: 14, padding: "16px 18px", marginBottom: 20 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10 }}>
                    {([[money(band.p25, moneyCA), "25th", false], ["", "Median", true], ["", "75th", true]] as [string, string, boolean][]).map(([v, l, locked]) => (
                      <div key={l}>
                        {locked ? (
                          <>
                            <div aria-hidden="true" style={{ fontSize: 18, fontWeight: 800, color: INK, letterSpacing: "-0.045em", filter: "blur(7px)", userSelect: "none" }}>$00,000</div>
                            <span className="afq-sr">{l}: shown in your plan</span>
                          </>
                        ) : (
                          <div style={{ fontSize: 18, fontWeight: 800, color: INK, letterSpacing: "-0.045em", whiteSpace: "nowrap" }}>{v.replace(" CAD", "")}</div>
                        )}
                        <div aria-hidden={locked} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: FAINT, marginTop: 2 }}>{l}{!locked && moneyCA ? ", CAD" : ""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <label htmlFor="afq-email" style={S.label}>Where should we send your alert?</label>
              <input id="afq-email" style={S.input} type="email" autoComplete="email" placeholder="you@email.com" value={a.email} autoFocus
                aria-describedby="afq-email-note"
                aria-invalid={alertError || undefined}
                onChange={(e) => setA((p) => ({ ...p, email: e.target.value }))} />
              {alertError && (
                <div role="alert" style={{ background: RED_BG, borderRadius: 11, padding: "12px 14px", marginTop: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: RED_TXT }}>We could not set up your alert</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: TEXT, marginTop: 3 }}>Nothing was sent. Check the address and try again. If it keeps failing, your connection may have dropped.</div>
                </div>
              )}
              <button type="submit" style={{ ...S.primary, marginTop: 12, opacity: emailOk ? 1 : 0.5 }}
                disabled={!emailOk || submitting}>{submitting ? "Setting up..." : alertError ? "Try again" : "Show my plan"}</button>
              <p id="afq-email-note" style={{ fontSize: 12, lineHeight: 1.5, color: FAINT, margin: "10px 0 0" }}>Every alert email has a one-click unsubscribe.</p>
            </>
          )}
        </div>
      )}

      {step === 11 && (
        <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <h2 ref={headingRef} tabIndex={-1} style={S.q}>Building your plan</h2>
          <p style={S.sub}>Each line lands as it finishes.</p>
          {scanLines.map((t, i) => (
            <div key={t} style={{ display: "grid", gridTemplateColumns: "20px minmax(0,1fr)", columnGap: 11, alignItems: "start", padding: "10px 0", borderTop: `1px solid ${HAIR}` }}>
              <div aria-hidden="true" style={{ width: 20, height: 20, borderRadius: 999, background: i < scanIdx ? GREEN : HAIR, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {i < scanIdx && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>}
              </div>
              <span style={{ fontSize: 14, lineHeight: 1.45, color: i <= scanIdx ? INK : "#9AAABA", fontWeight: i === scanIdx ? 700 : 500 }}>{t}</span>
            </div>
          ))}
        </div>
      )}

      {step >= 12 && (
        <div style={{ flexGrow: 1 }}>
          <h2 ref={headingRef} tabIndex={-1} style={S.q}>Your plan</h2>
          <p style={{ ...S.sub, marginBottom: 18 }}>
            Your {freq} alert for {roleName ?? "your title"} in {alertScope} is set. Everything else you told us shapes the plan below.
          </p>
          <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {plan.map((m, idx) => (
              <li key={m.h} style={{ borderTop: `1px solid ${HAIR}`, padding: "14px 0" }}>
                <div style={{ display: "grid", gridTemplateColumns: "26px minmax(0,1fr)", columnGap: 12, alignItems: "start" }}>
                  <div aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 8, background: GREEN_BG, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: GREEN }}>{idx + 1}</span>
                  </div>
                  <div>
                    <h3 style={{ fontSize: 15, fontWeight: 800, color: INK, letterSpacing: "-0.015em", margin: 0 }}>{m.h}</h3>
                    <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "#55677B", margin: "4px 0 0" }}>{m.b}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <p style={{ fontSize: 12.5, lineHeight: 1.5, color: FAINT, margin: "14px 0 0" }}>
            These numbers stay live. Come back to <a href="/get-started" style={{ color: GREEN_TXT, fontWeight: 700 }}>your co-pilot page</a> any time on this device to see what changed since today.
          </p>
          <a href={matchesHref} onClick={() => siteEvents.act("quiz_see_matches", { source })}
            style={{ ...S.primary, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 14, textDecoration: "none" }}>See today&apos;s matches</a>
          <a href="/sign-up" onClick={() => siteEvents.act("quiz_signup_click", { source })}
            className="afq-ghost" style={{ ...S.ghost, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}>Create a free account to save jobs</a>
          {onDone && <button type="button" className="afq-ghost" style={S.ghost} onClick={onDone}>Close</button>}
        </div>
      )}
    </form>
  );
}
